import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getBranches } from '../services/branchService';
import { getProductInventoryByBranch } from '../services/productService';
import { formatCurrency } from '../utils/format';

const ESTADOS = [
  { value: 'critico', label: 'Crítico', className: 'badge-red',   icon: 'bi-exclamation-octagon-fill' },
  { value: 'normal',  label: 'Normal',  className: 'badge-green', icon: 'bi-check-circle-fill' },
];

function getEstado(item) {
  if (item.belowMin) return ESTADOS[0];
  return ESTADOS[1];
}

export default function Inventario() {
  const [query, setQuery] = useState('');
  const [filterEstado, setFilterEstado] = useState('');
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [inventoryData, setInventoryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [error, setError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await getBranches();
        setBranches(data.filter(b => b.state));
      } catch {
        setError('Error al cargar las sucursales');
      } finally {
        setLoadingBranches(false);
      }
    })();
  }, []);

  const fetchInventory = useCallback(async (branchId) => {
    if (!branchId) {
      setInventoryData(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await getProductInventoryByBranch(branchId);
      setInventoryData(data);
    } catch {
      setError('Error al obtener el inventario');
      setInventoryData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  function handleBranchChange(value) {
    setSelectedBranch(value);
    setQuery('');
    setFilterEstado('');
    fetchInventory(value);
  }

  const products = inventoryData?.products || [];

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return products.filter(p => {
      const matchQuery = !q || p.product.name.toLowerCase().includes(q);
      const matchEstado = !filterEstado || getEstado(p).value === filterEstado;
      return matchQuery && matchEstado;
    });
  }, [query, filterEstado, products]);

  function clearFilters() {
    setQuery('');
    setFilterEstado('');
  }

  const hasFilters = query || filterEstado;

  const counts = useMemo(() => {
    const c = { critico: 0, normal: 0 };
    products.forEach(p => { c[getEstado(p).value] += 1; });
    return c;
  }, [products]);

  const totalValor = useMemo(() => (
    filtered.reduce((sum, item) => sum + (Number(item.product.cost) || 0) * (item.totalStock || 0), 0)
  ), [filtered]);

  return (
    <div className="page-fade" style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 22, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: 12, color: 'var(--orig-text-muted)', marginBottom: 3 }}>
            Existencias por sucursal y depósito
          </p>
          <h5 style={{
            fontWeight: 800, color: 'var(--orig-text)',
            fontFamily: 'var(--orig-font-display)', letterSpacing: 0.5, fontSize: 20,
          }}>
            Consulta de Inventario
          </h5>
        </div>
        {selectedBranch && products.length > 0 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span className="badge-orig badge-red">
              <i className="bi bi-exclamation-octagon-fill" style={{ fontSize: 10 }} />
              {counts.critico} crítico{counts.critico === 1 ? '' : 's'}
            </span>
            <span className="badge-orig badge-green">
              <i className="bi bi-check-circle-fill" style={{ fontSize: 10 }} />
              {counts.normal} normal{counts.normal === 1 ? '' : 'es'}
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="section-card" style={{ marginBottom: 20 }}>
        <div className="section-header">
          <h6>
            <i className="bi bi-funnel" style={{ color: 'var(--orig-green)' }} />
            Filtros de búsqueda
          </h6>
        </div>
        <div className="section-body" style={{ padding: '16px 20px' }}>
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-4">
              <label className="form-label">Sucursal</label>
              <select
                className="form-select"
                value={selectedBranch}
                onChange={e => handleBranchChange(e.target.value)}
                disabled={loadingBranches}
              >
                <option value="">{loadingBranches ? 'Cargando...' : 'Seleccione una sucursal'}</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-4">
              <label className="form-label">Producto</label>
              <div className="search-wrap">
                <i className="bi bi-search search-icon" />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar producto..."
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  disabled={!selectedBranch}
                />
                {query && (
                  <button className="clear-btn" onClick={() => setQuery('')} title="Limpiar">
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
            </div>

            <div className="col-6 col-md-2">
              <label className="form-label">Estado</label>
              <select
                className="form-select"
                value={filterEstado}
                onChange={e => setFilterEstado(e.target.value)}
                disabled={!selectedBranch}
              >
                <option value="">Todos</option>
                {ESTADOS.map(e => (
                  <option key={e.value} value={e.value}>{e.label}</option>
                ))}
              </select>
            </div>

            {hasFilters && (
              <div className="col-6 col-md-2">
                <button
                  className="btn-orig btn-orig-outline"
                  onClick={clearFilters}
                  style={{ width: '100%', justifyContent: 'center', padding: '9px 10px' }}
                  title="Limpiar filtros"
                >
                  <i className="bi bi-x-circle" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert-orig alert-error" style={{ marginBottom: 20 }}>
          <i className="bi bi-exclamation-triangle-fill" />
          {error}
        </div>
      )}

      {/* Results */}
      {!selectedBranch ? (
        <div className="section-card">
          <div style={{ padding: '50px 20px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
            <i className="bi bi-building" style={{ fontSize: 36, display: 'block', marginBottom: 12, opacity: 0.4 }} />
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Seleccione una sucursal</div>
            <div style={{ fontSize: 12 }}>Elija una sucursal para consultar su inventario de productos.</div>
          </div>
        </div>
      ) : loading ? (
        <div className="section-card">
          <div style={{ padding: '50px 20px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
            <div className="spinner" style={{ margin: '0 auto 14px' }} />
            <div style={{ fontSize: 13 }}>Cargando inventario...</div>
          </div>
        </div>
      ) : (
        <div className="section-card">
          <div className="section-header">
            <h6>
              <i className="bi bi-clipboard-data" style={{ color: 'var(--orig-green)' }} />
              Existencias — {inventoryData?.branch?.name}
            </h6>
            <span className="badge-orig badge-green">
              {filtered.length} {filtered.length === 1 ? 'producto' : 'productos'}
            </span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--orig-text-muted)' }}>
                <i className="bi bi-search" style={{ fontSize: 30, display: 'block', marginBottom: 10, opacity: 0.4 }} />
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Sin resultados</div>
                <div style={{ fontSize: 12 }}>
                  {hasFilters ? 'No hay productos que coincidan con los filtros.' : 'No hay inventario registrado en esta sucursal.'}
                </div>
                {hasFilters && (
                  <button
                    className="btn-orig btn-orig-ghost"
                    onClick={clearFilters}
                    style={{ marginTop: 14, fontSize: 12 }}
                  >
                    <i className="bi bi-x-circle" />
                    Limpiar filtros
                  </button>
                )}
              </div>
            ) : (
              <table className="orig-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Producto</th>
                    <th>Stock mínimo</th>
                    <th>Stock total</th>
                    <th>Costo</th>
                    <th>Valor total</th>
                    <th>Estado</th>
                    <th style={{ textAlign: 'center' }}>Depósitos</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, idx) => {
                    const estado = getEstado(item);
                    return (
                      <tr key={item.productId} style={estado.value === 'critico' ? { background: 'var(--orig-red-dim)' } : undefined}>
                        <td style={{ color: 'var(--orig-text-muted)', fontSize: 12, fontFamily: 'monospace' }}>
                          {idx + 1}
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{item.product.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>
                            {item.product.packageType} × {item.product.packageQuantity}
                          </div>
                        </td>
                        <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{item.stockMin}</td>
                        <td>
                          <span style={{
                            fontSize: 14, fontWeight: 700,
                            color: item.belowMin ? 'var(--orig-red-light)' : 'var(--orig-text)',
                          }}>
                            {item.totalStock}
                          </span>
                        </td>
                        <td style={{ fontSize: 12.5, color: 'var(--orig-text-muted)' }}>{formatCurrency(item.product.cost)}</td>
                        <td style={{ fontSize: 13, fontWeight: 600, color: 'var(--orig-text)' }}>
                          {formatCurrency(item.product.cost * item.totalStock)}
                        </td>
                        <td>
                          <span className={`badge-orig ${estado.className}`}>
                            <i className={`bi ${estado.icon}`} style={{ fontSize: 10 }} />
                            {estado.label}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button
                            className="btn-orig btn-orig-ghost"
                            onClick={() => setSelectedProduct(item)}
                            style={{ padding: '4px 10px', fontSize: 12 }}
                            title="Ver depósitos"
                          >
                            <i className="bi bi-box-seam" />
                            {item.deposits.length}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--orig-border)' }}>
                    <td colSpan={5} style={{ textAlign: 'right', fontSize: 12.5, fontWeight: 600, color: 'var(--orig-text-muted)' }}>
                      Total general
                    </td>
                    <td style={{ fontSize: 14, fontWeight: 800, color: 'var(--orig-green-bright)' }}>
                      {formatCurrency(totalValor)}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Modal de depósitos */}
      {selectedProduct && (
        <div
          onClick={() => setSelectedProduct(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 300,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="section-card"
            style={{ width: '100%', maxWidth: 520, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
          >
            <div className="section-header">
              <h6>
                <i className="bi bi-box-seam" style={{ color: 'var(--orig-green)' }} />
                Detalle por depósito
              </h6>
              <button
                type="button"
                onClick={() => setSelectedProduct(null)}
                style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
                title="Cerrar"
              >
                ×
              </button>
            </div>

            <div className="section-body" style={{ padding: '20px' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px', marginBottom: 20,
                background: 'var(--orig-panel)', borderRadius: 'var(--orig-radius)',
                border: '1px solid var(--orig-border)',
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 'var(--orig-radius)',
                  background: 'var(--orig-green-dim)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <i className="bi bi-box-fill" style={{ fontSize: 18, color: 'var(--orig-green-bright)' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--orig-text)' }}>
                    {selectedProduct.product.name}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--orig-text-muted)' }}>
                    {selectedProduct.product.packageType} × {selectedProduct.product.packageQuantity}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <div style={{
                  flex: 1, padding: '12px 14px',
                  background: 'var(--orig-green-dim)', borderRadius: 'var(--orig-radius)',
                  border: '1px solid rgba(5,150,105,0.2)',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginBottom: 2 }}>Stock total</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--orig-green-bright)' }}>
                    {selectedProduct.totalStock}
                  </div>
                </div>
                <div style={{
                  flex: 1, padding: '12px 14px',
                  background: 'var(--orig-panel)', borderRadius: 'var(--orig-radius)',
                  border: '1px solid var(--orig-border)',
                }}>
                  <div style={{ fontSize: 11, color: 'var(--orig-text-muted)', marginBottom: 2 }}>Stock mínimo</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--orig-text)' }}>
                    {selectedProduct.stockMin}
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orig-text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Distribución por depósito
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {selectedProduct.deposits.map(dep => {
                  return (
                    <div key={dep.depositId} style={{
                      padding: '12px 14px',
                      background: 'var(--orig-panel)', borderRadius: 'var(--orig-radius)',
                      border: '1px solid var(--orig-border)',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {dep.main && (
                            <span style={{
                              background: 'var(--orig-green-dim)',
                              color: 'var(--orig-green-bright)',
                              padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>
                              <i className="bi bi-star-fill" style={{ fontSize: 8 }} />
                              Principal
                            </span>
                          )}
                          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--orig-text)' }}>
                            {dep.depositName}
                          </span>
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--orig-text)' }}>
                          {dep.stock}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
